import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useLang, useT, pickLocalized } from '../i18n';
import type { Work, WorkKind } from '../types/work';

type Status = 'loading' | 'ready' | 'error';
type Category = 'all' | WorkKind;

const categories: Category[] = ['all', 'pcg', 'shader', 'character'];

interface WorksProps {
  onSelectWork: (id: string) => void;
}

export default function Works({ onSelectWork }: WorksProps) {
  const t = useT();
  const { lang } = useLang();
  const [status, setStatus] = useState<Status>('loading');
  const [works, setWorks] = useState<Work[]>([]);
  const [error, setError] = useState<string>('');
  const [category, setCategory] = useState<Category>('all');

  useEffect(() => {
    let cancelled = false;
    api
      .works()
      .then((data) => {
        if (cancelled) return;
        setWorks(data);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'unknown error');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () => (category === 'all' ? works : works.filter((w) => w.kind === category)),
    [works, category],
  );

  return (
    <section className="flex flex-col gap-8">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-3">
        <h2 className="text-3xl font-semibold text-cocoa">{t('works.title')}</h2>
        <span className="font-mono text-xs text-coffee/70">{t('works.apiHint')}</span>
      </header>

      <nav aria-label="categories" className="flex flex-wrap gap-2">
        {categories.map((cat) => {
          const active = cat === category;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              aria-pressed={active}
              className={[
                'rounded-full border px-3 py-1.5 font-mono text-xs shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15',
                active
                  ? 'border-neutral-950 bg-tan text-neutral-100'
                  : 'border-neutral-200 bg-white text-coffee hover:-translate-y-0.5 hover:border-neutral-300 hover:text-cocoa hover:shadow-soft',
              ].join(' ')}
            >
              {t(`works.category.${cat}`)}
            </button>
          );
        })}
      </nav>

      {status === 'loading' && <p className="text-coffee">{t('works.loading')}</p>}
      {status === 'error' && (
        <p className="text-cocoa font-mono text-sm italic">{t('works.error', { error })}</p>
      )}

      {status === 'ready' && (
        <div key={category} className="animate-fade-in">
          {filtered.length === 0 ? (
            <p className="text-coffee">{t('works.empty')}</p>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((work) => (
                <li key={work.id}>
                  <button
                    type="button"
                    onClick={() => onSelectWork(work.id)}
                    className="flex h-full w-full flex-col rounded-lg border border-neutral-200 bg-white p-5 text-left shadow-soft transition hover:-translate-y-1 hover:border-neutral-300 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
                  >
                    <div className="flex items-center justify-between text-xs font-mono text-coffee/70">
                      <span className="text-coffee">{t(`works.category.${work.kind}`)}</span>
                      <span>{work.year}</span>
                    </div>
                    <h3 className="mt-3 text-lg font-semibold text-cocoa">
                      {pickLocalized(work.title, lang)}
                    </h3>
                    <p className="mt-2 text-sm text-coffee leading-relaxed">
                      {pickLocalized(work.summary, lang)}
                    </p>
                    {work.tags.length > 0 && (
                      <ul className="mt-4 flex flex-wrap gap-1.5">
                        {work.tags.map((tag) => (
                          <li
                            key={tag}
                            className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-coffee/80"
                          >
                            {tag}
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
